import React, {useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Button, ButtonGroup, ButtonToolbar, IconButton, Loader} from "rsuite";
import {DndProvider} from "react-dnd";
import CalculationContext from "../context";
import CalculationTableHeader from "./table-header";
import {HTML5Backend} from "react-dnd-html5-backend";
import CalculationTableEntry from "./table-entry";
import {useDispatch} from "react-redux";
import {addEntry, useEntryIdsByGroup, useFlightGroup, useGroups} from "../store/entries-slice";
import {useCalculationServicesQuery, useOrganizationQuery} from "../../../store/api";
import {setField, useField} from "../store/fields-slice";
import {useChoiceColumns, useChoiceIds, useChoiceSettings} from "../store/choices-slice";
import {NoEntries} from "./table-no-entries";
import {UniversalEntitySelect} from "../../../dialog/universal-entity-select";
import CalculationTableFooter from "./table-footer";
import Action from "./action";
import TableColumns from "./table-columns";
import {
    updateManualReload,
    useColSpan,
    useColumnCount,
    useHeader,
    useHideOccupancyChoices,
    useLoading
} from "../store/state-slice";
import SioIcon from "../../../icon/rsuite-icon-font/SioIcon";
import {useCalculationDetails} from "../store/calculation-details";
import {ActionButton} from "../../../action/react/action-button";

function setSizes() {
    const prefix = ".calculation-services-table > thead"
    const element = document.querySelector(prefix + "> tr:first-child");

    if (!element) {
        return;
    }

    let height = element.offsetHeight
    document.querySelectorAll(prefix + "> tr:last-child > th").forEach(element => {
        element.style.top = height + "px"
    })
    height = document.querySelector(prefix).offsetHeight
    document.querySelectorAll(prefix + " th.occupancy-small > span").forEach(element => {
        element.style.width = height + "px"
    })

    const left = document.getElementById('navbar').offsetWidth + "px"
    document.querySelectorAll(
        ".calculation-services-table td.service, " +
        ".calculation-services-table tr.group > th.sticky"
    ).forEach(element => {
        element.style.left = left
    })
}

export default function CalculationTable() {
    const groups = useGroups()
    const loading = useLoading()
    const ref = useRef()
    const dispatch= useDispatch()
    const tableView = useField("tableView")

    const minParticipants = useField("minParticipants");
    const defaultMinParticipants = useField("defaultMinParticipants");
    const defaultMaxParticipants = useField("defaultMaxParticipants");

    useLayoutEffect(() => {
        setSizes()
    }, [loading])

    const {openInFullView} = useChoiceSettings()

    useEffect(() => {
        if(openInFullView){
            toggleCollapsed();
        }
        window.addEventListener('resize', setSizes)

        return () => {
            window.removeEventListener('resize', setSizes)
        }

    }, [])

    return (
        <div className="col-xs-12">
            <div className="table-responsive">
                <DndProvider backend={HTML5Backend}>
                    <table ref={ref} className={`table table-condensed table-hover calculation-services-table ${tableView}`}>
                        <CalculationTableHeader toggleCollapsed={toggleCollapsed}/>
                        {groups.map(group => <TableGroup key={group} group={group}/>)}
                        <CalculationTableFooter/>
                    </table>
                </DndProvider>
            </div>
        </div>
    )

    function toggleCollapsed() {
        if (tableView == "collapsed") {
            dispatch(setField("tableView", "non-collapsed"));
            dispatch(updateManualReload(true));
        }else{
            dispatch(setField("tableView", "collapsed"));
            dispatch(updateManualReload(true));
        }
    }
}

function EditAdditionalService() {
    const calculationId = useField("id")
    const {refetch} = useCalculationDetails()
    return (
        <ActionButton
            config={{
                type: "workflow",
                workflowId: "tourism-journey-calculation/service-edit-workflow",
                formId: "tourism-journey-calculation/service-edit-form",
                label: <i className="fa fa-pencil"/>,
            }}
            appearance="link"
            context={{
                id: calculationId,
                modelId: "tourism-journey-calculation/calculation",
                data: {
                    formData: {
                        calculation: {
                            id: calculationId,
                            modelId: "tourism-journey-calculation/calculation",
                        },
                    },
                },
            }}
            size={"xs"}
            style={{
                background: 'transparent',
                border: 'none',
                color: 'blue'
            }}
        />
    )
}

function TableGroup({group}) {
    const dispatch = useDispatch()
    const {i18n} = useContext(CalculationContext)
    const colSpan = useColSpan()
    const columnCount = useColumnCount()
    const hideOccupancyChoices = useHideOccupancyChoices()
    const choiceColumns = useChoiceColumns()
    const ids = useEntryIdsByGroup(group)
    const flightGroup = useFlightGroup()
    const {organization} = useContext(CalculationContext)
    const {data: organizationData } = useOrganizationQuery(organization)
    const alignment = organizationData?.settings?.tourismJourneyCalculationSettings?.alignment ?? 'left'

    return (
        <tbody>
            <tr className="group">
                {alignment === "right" ? (
                    <th
                        colSpan={colSpan + (hideOccupancyChoices ? 0 : choiceColumns) + columnCount}
                        className="sticky"
                        style={{ textAlign: alignment }}
                    >
                        {"additionalService" === group ? (
                            <>
                                {i18n.tr("tourism.additionalServices") + " "}
                                <EditAdditionalService
                                    i18n={i18n}
                                    group={group}
                                    dispatch={dispatch}
                                />
                            </>
                        ) : (
                            group
                        )}
                        {flightGroup === group && (
                            <span className="fa fa-plane" style={{ marginLeft: ".25em" }} />
                        )}
                    </th>
                ) : (
                    <>
                        <th colSpan={colSpan} className="sticky">
                            {"additionalService" === group ? (
                                <>
                                    {i18n.tr("tourism.additionalServices") + " "}
                                    <EditAdditionalService
                                        i18n={i18n}
                                        group={group}
                                        dispatch={dispatch}
                                    />
                                </>
                            ) : (
                                group
                            )}
                            {flightGroup === group && (
                                <span className="fa fa-plane" style={{ marginLeft: ".25em" }} />
                            )}
                        </th>
                        <th colSpan={(hideOccupancyChoices ? 0 : choiceColumns) + columnCount} />
                    </>
                )}
            </tr>

            {ids.length ? ids.map(id => (
                <CalculationTableEntry key={id} id={id} group={group}/>
            )) : (
                <NoEntries group={group}/>
            )}

            <tr>
                <td colSpan={colSpan} className="border-right">
                    <ButtonToolbar>
                        <ButtonGroup>
                            <CreateAction group={group}/>
                        </ButtonGroup>
                        <ServicesGroup group={group}/>
                    </ButtonToolbar>
                </td>
                {!hideOccupancyChoices && <td colSpan={choiceColumns} className="border-right"/>}
                <TableColumns/>
            </tr>
        </tbody>
    )
}

function CreateAction({group}) {
    const dispatch = useDispatch();
    const {viewActionHandler} = useContext(CalculationContext);
    const occupancyChoices = useChoiceIds();
    const [first, second] = useHeader();
    const calculationId = useField("id");
    const dateRanges = useField("dateRanges");
    const active = !!useField("active");

    const addActionContext = useMemo(
        () => ({
            moduleId: "price",
            viewId: "price-table",
            modal: true,
            title: "Preistabelle erstellen",
            bulk: true,
            actionContext: {group},
            calculationColumns: first,
            contextObjectRef: {
                id: calculationId,
                modelId: 'tourism-journey-calculation/calculation',
                bindValues: [
                    {property: 'dateRanges', value: dateRanges}
                ],
            }
        }),
        [group, first]
    )

    function afterCreate(service) {
        dispatch(addEntry({service: {id: service.id, modelId: service.modelId}, group, occupancyChoices, identifier: service.id}))
        dispatch(updateManualReload(true))
    }

    return (
        <Action icon={<SioIcon icon=" fa fa-plus"/>} color="green" size="xs"
                handler={viewActionHandler}
                context={addActionContext}
                disabled={active}
                onResult={afterCreate}>
            Preistabelle
        </Action>
    )
}

function ServicesGroup({group}) {
    const dispatch = useDispatch()
    const {i18n, dialog, flash, standardActions} = useContext(CalculationContext)
    const occupancyChoices = useChoiceIds()
    const {isLoading, data: services = []} = useCalculationServicesQuery()
    const active = !!useField("active")
    const calculationId = useField("id")
    const [loading, setLoading] = useState(false)

    return isLoading ? <Loader/> : (
        <ButtonGroup>
            {services.map((service, idx) => {
                const label = i18n.tr(service.label)
                const buttonProps = {
                    key: service.value,
                    size: "xs",
                    onClick: () => addService(service),
                    loading,
                    disabled: active
                }

                return 0 === idx ? (
                    <IconButton {...buttonProps} icon={<SioIcon icon={" fa fa-table-list"}/>}>
                        {label}
                    </IconButton>
                ) : (
                    <Button {...buttonProps}>{label}</Button>
                );
            })}
        </ButtonGroup>
    )

    function addService(serviceConfig) {
        setLoading(true)

        const title = i18n.tr(serviceConfig.label) + " hinzufügen";

        dialog.open({
            viewModel: UniversalEntitySelect,
            model: {
                selectModelId: serviceConfig.value,
                title: title,
                //Not supported with form atm
                multiSelect: false,
                conditions: {}
            }
        }).whenClosed(({wasCancelled, output}) => {

            if (!wasCancelled && output.length) {

                const service = {id: output[0].id, modelId: output[0].modelId};

                const data = {
                    service: service
                };

                if (serviceConfig.hasFormFields) {

                    return standardActions.getAction({
                        type: 'workflow',
                        workflowId: 'tourism-journey-calculation/service-add',
                        formId: 'tourism-journey-calculation/service-add'
                    }, {data: {formData: data}, id: calculationId}).action().then((result) => {

                        if (result?.[0]?.data) {

                            console.log('OCCUPANCY', occupancyChoices, calculationId);

                            for (const service of result?.[0]?.data) {
                                service.group = group;
                                dispatch(addEntry(service))
                            }
                             dispatch(updateManualReload(true))

                        } else {
                            flash.error('Leistung konnte nicht eingefügt werden');
                        }
                        setLoading(false);
                        dispatch(updateManualReload(true))
                    }).catch((e) => {
                        flash.error(e.message ?? 'Leistung konnte nicht eingefügt werden');
                        setLoading(false);
                    });

                } else {
                    for (const {id, modelId} of output) {
                        dispatch(addEntry({service: {id, modelId}, group, occupancyChoices, identifier: identifier}))
                    }
                    setLoading(false)
                }
            } else {
                setLoading(false)
            }

        }).catch(() => {
            setLoading(false)
        })
    }
}
